import { lei, type CommitSeries, t, intlLocale } from '@procumi/shared';
import { useState } from 'react';

/**
 * Achiziționat / angajat / planificat, pe luni: trecutul și următoarele luni.
 *
 * Coloană stivuită, de jos în sus: albastru plin = achiziționat (marfa a
 * venit), mov = angajat (comandă emisă, marfa încă n-a venit), contur punctat
 * = planificat (ales fără comandă sau cerere deschisă cu ofertă). Albastrul
 * rămâne accentul; movul e a doua serie, ca în restul graficelor. Cifrele exacte
 * se citesc în casetă, care stă pe luna curentă și se mută după mouse.
 */

const scurt = new Intl.NumberFormat(intlLocale(), { notation: 'compact', maximumFractionDigits: 1 });

export function CommitMonths({ series, currentKey }: { series: CommitSeries; currentKey: string }) {
  const { months } = series;
  const [hover, setHover] = useState<string | null>(null);
  const ales = hover ?? currentKey;

  const sum = (m: (typeof months)[number]) => m.bought + m.committed + m.planned;
  const max = Math.max(...months.map(sum), 0);
  if (max === 0) {
    return (
      <div className="empty" style={{ padding: 'var(--space-6)' }}>
        {t(
          'Nicio comandă emisă și nicio ofertă de estimat încă. Graficul se umple din comenzile tale: ce a venit pe șantier, ce e comandat și ce urmează.',
        )}
      </div>
    );
  }

  const n = months.length;
  const i = months.findIndex((m) => m.key === ales);
  const m = months[i];
  const pct = (v: number) => (v > 0 ? Math.max((v / max) * 100, 1.5) : 0);
  const curent = m?.key === currentKey;

  return (
    <div className="months">
      <div className="months-chart">
        <div className="months-axis" aria-hidden>
          <span>{scurt.format(max)}</span>
          <span>{scurt.format(max / 2)}</span>
          <span>0</span>
        </div>
        <div className="months-plot" onMouseLeave={() => setHover(null)}>
          <span className="months-grid" aria-hidden />
          {months.map((x) => (
            <button
              type="button"
              className={`month cm${x.key === ales ? ' on' : ''}${x.future ? ' future' : ''}`}
              key={x.key}
              onMouseEnter={() => setHover(x.key)}
              onFocus={() => setHover(x.key)}
              onClick={() => setHover(x.key)}
              aria-label={t(
                '{0}: achiziționat {1}, angajat {2}, planificat {3}',
                x.name,
                lei(x.bought),
                lei(x.committed),
                lei(x.planned),
              )}
            >
              <span className="month-track cm-track">
                {/* `column-reverse`: primul element stă jos, deci achiziționatul e primul */}
                <span className="cm-stack">
                  {x.bought ? (
                    <span className="cm-seg bought" style={{ height: `${pct(x.bought)}%` }} />
                  ) : null}
                  {x.committed ? (
                    <span className="cm-seg commit" style={{ height: `${pct(x.committed)}%` }} />
                  ) : null}
                  {x.planned ? (
                    <span className="cm-seg plan" style={{ height: `${pct(x.planned)}%` }} />
                  ) : null}
                </span>
              </span>
              <span className="month-name">{x.label}</span>
            </button>
          ))}
          {m ? (
            <div
              className={`month-tip${i === 0 ? ' first' : i === n - 1 ? ' last' : ''}`}
              style={{ left: `${((i + 0.5) / n) * 100}%` }}
            >
              <b className="tip-title">{m.name}</b>
              <span className="tip-row">
                <i className="key spend" />
                {t('Achiziționat')} <b>{lei(m.bought)}</b>
              </span>
              <span className="tip-row">
                <i className="key commit" />
                {t('Angajat')} <b>{lei(m.committed)}</b>
              </span>
              <span className="tip-row">
                <i className="key plan" />
                {t('Planificat')} <b>{lei(m.planned)}</b>
              </span>
              {curent && (series.committedUndated || series.plannedUndated) ? (
                <span className="tip-note">
                  {t(
                    'include fără dată: {0}',
                    [
                      series.committedUndated ? t('{0} angajat', lei(series.committedUndated)) : '',
                      series.plannedUndated ? t('{0} planificat', lei(series.plannedUndated)) : '',
                    ]
                      .filter(Boolean)
                      .join(', '),
                  )}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
      {series.plannedUnknown ? (
        <p className="hint" style={{ margin: 'var(--space-2) 0 0' }}>
          {series.plannedUnknown === 1
            ? t('O cerere nu are încă ofertă completă, deci nu intră la planificat.')
            : t('{0} cereri nu au încă ofertă completă, deci nu intră la planificat.', series.plannedUnknown)}
        </p>
      ) : null}
    </div>
  );
}
